import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchPrompts, createPrompt, updatePrompt, deletePrompt, fetchTeam, updateTeam } from '../services/api';
import TabNavigation, { TABS } from '../components/TabNavigation';
import Header from '../components/Header';
import '../styles/Prompts.css';

function Prompts({ headlessMode = false, selectedDomain: propSelectedDomain, selectedRegion: propSelectedRegion }) {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editPrompt, setEditPrompt] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(propSelectedDomain || 'Legal');
  const [selectedRegion, setSelectedRegion] = useState(propSelectedRegion || 'AUS');
  
  // Update local state when props change (when in headless mode)
  useEffect(() => {
    if (headlessMode) {
      if (propSelectedDomain) setSelectedDomain(propSelectedDomain);
      if (propSelectedRegion) setSelectedRegion(propSelectedRegion);
    }
  }, [headlessMode, propSelectedDomain, propSelectedRegion]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    template: '',
    description: '',
    category: '',
    tags: ''
  });
  
  const domains = ['Legal', 'Medical'];
  const regions = ['AUS', 'NZL', 'MEX', 'GBR'];

  const { userProfile, logout, switchTeam } = useAuth();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('authToken');
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };
  
  const handleTeamChange = async (e) => {
    const newTeamId = e.target.value;
    if (newTeamId && newTeamId !== userProfile.teamId) {
      const success = switchTeam(newTeamId);
      if (success) {
        // Reload prompts for the new team
        setPrompts([]);
        setLoading(true);
        setError(null);
        
        // Fetch team details to update locale and domain
        try {
          const teamDetails = await fetchTeam(newTeamId);
          console.log("Team details fetched in Prompts:", teamDetails);
          
          // Update locale dropdown if available
          if (teamDetails.teamLocale) {
            setSelectedRegion(teamDetails.teamLocale);
          }
          
          // Update domain dropdown if available
          if (teamDetails.llmDomain) {
            // Convert API's uppercase format to title case for display
            const domain = teamDetails.llmDomain.charAt(0).toUpperCase() + 
                          teamDetails.llmDomain.slice(1).toLowerCase();
            setSelectedDomain(domain);
          }
        } catch (err) {
          console.error("Error fetching team details in Prompts:", err);
          // Don't set error here as we still want to load prompts
        }
      }
    }
  };
  
  const handleDomainChange = async (e) => {
    const newDomain = e.target.value;
    setSelectedDomain(newDomain);
    
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No team ID available to update domain');
      setError('Team information is missing. Cannot update domain.');
      return;
    }
    
    try {
      console.log(`Updating team domain to ${newDomain}`);
      // Update team with new domain (llmDomain in the API)
      await updateTeam(userProfile.teamId, {
        llmDomain: newDomain.toUpperCase() // API expects uppercase (LEGAL or MEDICAL)
      });
      console.log('Domain updated successfully');
      setSuccessMessage(`Domain updated to ${newDomain}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating team domain:', err);
      setError('Failed to update team domain: ' + (err.response?.data?.message || err.message));
    }
  };
  
  const handleRegionChange = async (e) => {
    const newRegion = e.target.value;
    setSelectedRegion(newRegion);
    
    // Clear any previous messages
    setError(null);
    setSuccessMessage(null);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No team ID available to update locale');
      setError('Team information is missing. Cannot update region.');
      return;
    }
    
    try {
      console.log(`Updating team locale to ${newRegion}`);
      // Update team with new locale (teamLocale in the API)
      await updateTeam(userProfile.teamId, {
        teamLocale: newRegion // Using the country code directly (AUS, NZL, US, GBR)
      });
      console.log('Region updated successfully');
      setSuccessMessage(`Region updated to ${newRegion}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating team locale:', err);
      setError('Failed to update team region: ' + (err.response?.data?.message || err.message));
    }
  };

  // Define loadPrompts as a component-level function that can be called from anywhere
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadPrompts = async () => {
    if (!userProfile || !userProfile.teamId) {
      console.error('No userProfile or teamId found. Cannot load prompts.');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching prompts for team:', userProfile.teamId);
      const promptData = await fetchPrompts(userProfile.teamId);
      console.log("Received prompt data:", promptData);
      
      // Ensure we're setting an array to state
      if (Array.isArray(promptData)) {
        setPrompts(promptData);
      } else {
        console.error("Expected array but got:", typeof promptData, promptData);
        setPrompts([]);
        setError("Invalid prompt data format received from server");
      }
    } catch (err) {
      console.error('Error loading prompts:', err);
      setError('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch team details and initialize domain and region on load
  useEffect(() => {
    const loadTeamDetails = async () => {
      if (userProfile && userProfile.teamId) {
        try {
          console.log("Fetching team details for initial load in Prompts:", userProfile.teamId);
          const teamDetails = await fetchTeam(userProfile.teamId);
          console.log("Team details fetched on initial load in Prompts:", teamDetails);
          
          // Update locale dropdown if available
          if (teamDetails.teamLocale) {
            setSelectedRegion(teamDetails.teamLocale);
          }
          
          // Update domain dropdown if available
          if (teamDetails.llmDomain) {
            // Convert API's uppercase format to title case for display
            const domain = teamDetails.llmDomain.charAt(0).toUpperCase() + 
                          teamDetails.llmDomain.slice(1).toLowerCase();
            setSelectedDomain(domain);
          }
        } catch (err) {
          console.error("Error fetching team details on initial load in Prompts:", err);
          
          // Fallback to teamSettings if available in user profile
          if (userProfile.teamSettings) {
            if (userProfile.teamSettings.llmDomain) {
              // Convert API's uppercase format to title case for display
              const domain = userProfile.teamSettings.llmDomain.charAt(0).toUpperCase() + 
                            userProfile.teamSettings.llmDomain.slice(1).toLowerCase();
              setSelectedDomain(domain);
            }
            
            if (userProfile.teamSettings.teamLocale) {
              setSelectedRegion(userProfile.teamSettings.teamLocale);
            }
          }
        }
      }
    };
    
    loadTeamDetails();
  }, [userProfile]);
  
  // Use useEffect to load prompts when component mounts or userProfile changes
  useEffect(() => {
    loadPrompts();
  }, [userProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const openModal = (prompt = null) => {
    if (prompt) {
      // Check if prompt is read-only, if so, don't open the modal
      if (prompt.isReadOnly) {
        console.log('Cannot edit a read-only prompt');
        return;
      }
      
      // Edit mode
      setEditPrompt(prompt);
      setFormData({
        title: prompt.title || '',
        template: prompt.template || '',
        description: prompt.description || '',
        category: prompt.category || '',
        tags: prompt.tags ? prompt.tags.join(', ') : ''
      });
    } else {
      // Create mode
      setEditPrompt(null);
      setFormData({
        title: '',
        template: '',
        description: '',
        category: '',
        tags: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditPrompt(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear any previous errors
    
    console.log('Form submitted with data:', formData);
    console.log('Current user profile:', userProfile);
    
    if (!userProfile || !userProfile.teamId) {
      console.error('No userProfile or teamId found. Cannot save prompt.');
      setError('User profile or team ID not found');
      return;
    }
    
    console.log('Team ID for prompt creation:', userProfile.teamId);
    
    // Log current prompts to see what we already have
    console.log('Current prompts in state:', prompts);

    try {
      const promptData = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()) : []
      };

      let result;
      if (editPrompt) {
        // Update existing prompt
        console.log('Updating prompt:', editPrompt.id);
        result = await updatePrompt(userProfile.teamId, editPrompt.id, promptData);
        console.log('Prompt updated successfully:', result);
        setPrompts(prompts.map(p => p.id === editPrompt.id ? result : p));
      } else {
        // Create new prompt
        console.log('Creating new prompt with data:', promptData);
        result = await createPrompt(userProfile.teamId, promptData);
        console.log('Prompt created successfully:', result);
        
        // Make sure the result is a valid object before adding to state
        if (result && typeof result === 'object' && result.id) {
          setPrompts(Array.isArray(prompts) ? [...prompts, result] : [result]);
        } else {
          console.error('Invalid response from createPrompt:', result);
          throw new Error('Invalid response from server when creating prompt');
        }
      }

      // Only close the modal if we get here successfully
      closeModal();
      
      // Force a refresh of prompts to make sure we get the latest from the server
      console.log('Forcing refresh of prompts after creation/update');
      loadPrompts();
    } catch (err) {
      console.error('Error saving prompt:', err);
      console.error('Error details:', {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message
      });
      
      // Create more descriptive error message
      let errorMsg = 'Failed to save prompt';
      if (err.response?.data?.message) {
        errorMsg += `: ${err.response.data.message}`;
      } else if (err.message) {
        errorMsg += `: ${err.message}`;
      }
      
      setError(errorMsg);
      
      // Keep modal open when there's an error
      // closeModal(); - Comment this out to keep the form open on error
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) {
      return;
    }

    try {
      await deletePrompt(userProfile.teamId, promptId);
      setPrompts(prompts.filter(p => p.id !== promptId));
    } catch (err) {
      console.error('Error deleting prompt:', err);
      setError('Failed to delete prompt');
    }
  };

  return (
    <div className="prompts-container">
      {!headlessMode && (
        <>
          <Header 
            showTeamSelector={true}
            showDomainSelector={true}
            showRegionSelector={true}
            selectedDomain={selectedDomain}
            selectedRegion={selectedRegion}
            domains={domains}
            regions={regions}
            onDomainChange={handleDomainChange}
            onRegionChange={handleRegionChange}
            onTeamChange={handleTeamChange}
            onLogout={handleLogout}
          />

          <TabNavigation activeTab={TABS.QUICK_ACTIONS} />
        </>
      )}

      <div className="prompts-content">
        <div className="content-header">
          <h2>Author and Manage Quick Actions</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className="create-button"
              onClick={() => openModal()}
            >
              Create New Prompt
            </button>
            <button
              style={{ background: '#555', padding: '8px 15px', borderRadius: '4px', color: 'white', border: 'none' }}
              onClick={() => {
                console.log('Manual reload clicked');
                loadPrompts();
              }}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {loading ? (
          <div className="loading">Loading prompts...</div>
        ) : (
          <div className="prompts-list">
            {!Array.isArray(prompts) ? (
              <div className="error-message">Error: Invalid prompts data</div>
            ) : prompts.length === 0 ? (
              <div className="no-prompts">No prompts found</div>
            ) : (
              prompts.map(prompt => (
                <div key={prompt.id} className={`prompt-card ${prompt.isReadOnly ? 'read-only-prompt' : ''}`}>
                  <div className="prompt-header">
                    <h3>{prompt.title}</h3>
                    <div className="prompt-category">{prompt.category}</div>
                    {prompt.isReadOnly && <div className="read-only-badge">Read Only</div>}
                  </div>
                  
                  <p className="prompt-description">{prompt.description}</p>
                  
                  {prompt.tags && prompt.tags.length > 0 && (
                    <div className="prompt-tags">
                      {prompt.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  
                  <div className="prompt-actions">
                    {!prompt.isReadOnly && (
                      <>
                        <button 
                          className="edit-button"
                          onClick={() => openModal(prompt)}
                        >
                          Edit
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDelete(prompt.id)}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    {prompt.isReadOnly && (
                      <span className="read-only-message">This prompt cannot be edited</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="prompt-modal">
            <h2>{editPrompt ? 'Edit Prompt' : 'Create New Prompt'}</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input 
                  type="text" 
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <input 
                  type="text" 
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="category">Category</label>
                <input 
                  type="text" 
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="template">Template</label>
                <textarea 
                  id="template"
                  name="template"
                  value={formData.template}
                  onChange={handleInputChange}
                  rows={6}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="tags">Tags (comma separated)</label>
                <input 
                  type="text" 
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="tag1, tag2, tag3"
                />
              </div>
              
              <div className="modal-actions">
                <button type="button" className="cancel-button" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  {editPrompt ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Prompts;